import { CheckIcon } from '@heroicons/react/24/outline';
import { useReviewDmPatientMessages } from '../../../hooks/useReviewDmPatientMessages';
import type { DmLastResolution } from '../../../services/dmNotifications';
import { closureText } from '../notifications/view/resolvedNotificationText';
import { DonePill } from '../shared/DonePill';

type MarkAllReviewedVariant = 'outline' | 'primary';

// 'primary' copies the Conversations panel's green "Mark as read" button.
const VARIANT_STYLES: Record<
    MarkAllReviewedVariant,
    { button: string; icon: string; spinner: string }
> = {
    outline: {
        button: 'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 text-xs font-medium text-teal-700 transition-colors hover:bg-teal-50 disabled:cursor-default disabled:opacity-60 disabled:hover:bg-white',
        icon: 'h-3.5 w-3.5 flex-shrink-0',
        spinner:
            'h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-teal-600 border-t-transparent'
    },
    primary: {
        button: 'flex items-center gap-1.5 whitespace-nowrap bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        icon: 'w-4 h-4 flex-shrink-0',
        spinner: 'h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin'
    }
};

interface MarkAllReviewedButtonProps {
    patientId: string;
    // Null when unknown: the button stays usable rather than guessing there's nothing left.
    unreviewedCount: number | null;
    // With nothing left to review, a recent closure shows as Done instead of the button.
    lastResolution: DmLastResolution | null;
    // Lets a list fold the patient's row away before its refetch.
    onReviewed?: (patientId: string) => Promise<void>;
    variant?: MarkAllReviewedVariant;
}

export function MarkAllReviewedButton({
    patientId,
    unreviewedCount,
    lastResolution,
    onReviewed,
    variant = 'outline'
}: MarkAllReviewedButtonProps) {
    const review = useReviewDmPatientMessages({ onReviewed });
    if (unreviewedCount === 0 && lastResolution) {
        return <DonePill detail={closureText(lastResolution)} />;
    }
    const disabled = review.isPending || unreviewedCount === 0;
    const styles = VARIANT_STYLES[variant];
    return (
        <button
            type="button"
            aria-busy={review.isPending}
            disabled={disabled}
            onClick={() => review.mutate(patientId)}
            className={styles.button}
        >
            {review.isPending ? (
                <span aria-hidden="true" className={styles.spinner} />
            ) : (
                <CheckIcon aria-hidden="true" className={styles.icon} />
            )}
            Mark all reviewed
        </button>
    );
}
